import { ExclusionList, FormBlock, FormCard } from '@qbe/ads-blocks-forms';
import { Alert, Box, Button, Spacer, Spinner, Typography } from '@qbe/ads-core';
import React, { useEffect } from 'react';

import BlanketCover from '~/components/BlanketCover';
import SpecifiedItems from '~/components/SpecifiedItems';
import TotalCovers from '~/components/TotalCovers';
import { SPECIFIED_ITEM_LIMIT, TOTAL_COVER_VALUE_LIMIT } from '~/constants/domain-specific';
import {
  useAppContent,
  useAppDispatch,
  useBusinessDetails,
  useCoverDetails,
  useCoverDetailsEffects,
} from '~/hooks';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { addValidatedCover, removeValidatedCover } from '~/slices/selectCover';
import { qalFormCompleteAction, qalTrackEventAction } from '~/thunks/analytics';
import { Cover } from '~/types/cover';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';
import { CurrencyFormat, currencyFormatter } from '~/utils/currencyFormatter';

interface CoverDetailsPortableItemsProps {}

const CoverDetailsPortableItems: React.FC<CoverDetailsPortableItemsProps> = () => {
  const dispatch = useAppDispatch();
  const { industry } = useBusinessDetails();
  const { totalCoverValue, blanketCovers, specifiedItems } = useCoverDetails();
  const specifiedItemLimit = industry?.limits?.specifiedItemMin ?? SPECIFIED_ITEM_LIMIT;
  const totalCoverValueLimit =
    industry?.limits?.totalPortableItemSumInsured ?? TOTAL_COVER_VALUE_LIMIT;

  const { quotePremiums, isLoading, buttonText } = useCoverDetailsEffects({
    cover: Cover.PortableItems,
  });

  const isMaxLimitError = totalCoverValue !== null && totalCoverValue > TOTAL_COVER_VALUE_LIMIT;
  const isError = isMaxLimitError || (!blanketCovers?.length && !specifiedItems?.length);

  const handleFormComplete = () => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  };

  useEffect(() => {
    if (isError) {
      dispatch(removeValidatedCover(Cover.PortableItems));

      if (isMaxLimitError)
        dispatch(
          qalTrackEventAction({
            event: 'business_warning',
            eventAction: 'Portable items maximum exceeded warning',
            eventLabel: 'You have exceeded the maximum total amount',
          })
        );
    } else {
      dispatch(addValidatedCover(Cover.PortableItems));
    }
  }, [totalCoverValue, blanketCovers, specifiedItems, isError, dispatch, isMaxLimitError]);

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  const handleNext = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    quotePremiums();
    handleFormComplete();
  };

  const { getTextContent: getSharedContent } = useAppContent({
    basePath: 'shared',
  });

  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.coverDetailsPortableItems',
    tokens: {
      specifiedItemLimit: () => currencyFormatter(specifiedItemLimit - 1, CurrencyFormat.Comma),
      totalCoverValueLimit: () => currencyFormatter(totalCoverValueLimit, CurrencyFormat.Comma),
      coverValueLimitError: () => currencyFormatter(TOTAL_COVER_VALUE_LIMIT, CurrencyFormat.Comma),
    },
  });

  return (
    <FormCard variant="sectionTitle" title={getTextContent('title')}>
      <Spinner loading={isLoading} description={getSharedContent('loadingLabel')} />

      <Box textAlign="center">
        <Typography>{getJSXContent('content.description.description')}</Typography>
      </Box>

      <Spacer size="md" />
      <ExclusionList
        list={[
          {
            id: 'e1',
            item: 'Hired out property',
          },
          {
            id: 'e2',
            item: 'Firearms, pistols, guns and weapons',
          },
          {
            id: 'e3',
            item: 'Unmanned aircraft systems when in use',
          },
          {
            id: 'e4',
            item: 'Motorised plant and equipment (including their accessories)',
          },
          {
            id: 'e5',
            item: 'Any other items that do not fit into the categories available for selection below',
          },
        ]}
      />
      <Spacer size="md" />
      <BlanketCover />
      <Spacer size="xl" />

      <SpecifiedItems />

      <TotalCovers maximumLimit={totalCoverValueLimit} />

      {isMaxLimitError && (
        <>
          <Spacer size="md" />
          <Alert
            heading={getTextContent('fields.totalCovers.coverValueLimitError')}
            variant="error"
          />
        </>
      )}

      <FormBlock preset="button">
        <Button
          label={buttonText}
          data-track={`${buttonText} - portable items`}
          onClick={handleNext}
          disabled={isError || isLoading}
        />
      </FormBlock>
    </FormCard>
  );
};

export default CoverDetailsPortableItems;
